import { GithubRepo, ManualProject } from './github.types';
import { MANUAL_PROJECTS } from '../data/manualProjects';

/**
 * Deduplicate and merge dynamic GitHub repositories with manually curated project entries.
 *
 * Matching is performed case-insensitively against name, fullName, or normalized htmlUrl.
 * When matched, curated manual fields (such as a preferred live site URL, custom description,
 * custom topics, or featured status) take precedence while preserving GitHub dynamic metrics
 * (stars, forks, pushed date).
 *
 * Unmatched manual projects (e.g. non-GitHub or external sites) are synthesized into GithubRepo objects.
 */
export function mergeProjects(
  githubProjects: GithubRepo[],
  manualProjects: ManualProject[] = MANUAL_PROJECTS,
): GithubRepo[] {
  const manualMap = new Map<string, ManualProject>();
  for (const m of manualProjects) {
    if (m.name) manualMap.set(m.name.toLowerCase(), m);
    if (m.fullName) manualMap.set(m.fullName.toLowerCase(), m);
    if (m.htmlUrl) manualMap.set(m.htmlUrl.toLowerCase().replace(/\/+$/, ''), m);
  }

  const matchedManualNames = new Set<string>();

  const mergedGithub = githubProjects.map((gh) => {
    const keyName = gh.name.toLowerCase();
    const keyFullName = gh.fullName ? gh.fullName.toLowerCase() : '';
    const keyUrl = gh.htmlUrl ? gh.htmlUrl.toLowerCase().replace(/\/+$/, '') : '';

    const manual =
      (keyFullName ? manualMap.get(keyFullName) : undefined) ||
      manualMap.get(keyName) ||
      (keyUrl ? manualMap.get(keyUrl) : undefined);

    if (!manual) {
      return {
        ...gh,
        isFeatured: gh.isFeatured ?? gh.isPinned ?? false,
      };
    }

    matchedManualNames.add(manual.name.toLowerCase());

    const mergedTopics = Array.from(new Set([...(gh.topics || []), ...(manual.topics || [])]));

    return {
      ...gh,
      name: manual.name || gh.name,
      fullName: manual.fullName || gh.fullName,
      description: manual.description !== undefined ? manual.description : gh.description,
      htmlUrl: manual.htmlUrl || gh.htmlUrl,
      homepage: manual.homepage !== undefined ? manual.homepage : gh.homepage,
      language: manual.language || gh.language,
      topics: mergedTopics,
      isPinned: manual.isPinned !== undefined ? manual.isPinned : gh.isPinned,
      isFeatured:
        manual.isFeatured !== undefined
          ? manual.isFeatured
          : (gh.isFeatured ?? gh.isPinned ?? false),
    };
  });

  const unmatchedManual: GithubRepo[] = manualProjects
    .filter((m) => !matchedManualNames.has(m.name.toLowerCase()))
    .map((m, idx) => ({
      id: m.id ?? `manual-${m.name}-${idx}`,
      name: m.name,
      fullName: m.fullName || m.name,
      description: m.description ?? null,
      htmlUrl: m.htmlUrl || m.homepage || '',
      homepage: m.homepage ?? null,
      language: m.language ?? null,
      stargazersCount: 0,
      forksCount: 0,
      isFork: false,
      isArchived: false,
      updatedAt: new Date().toISOString(),
      topics: m.topics || [],
      isPinned: m.isPinned ?? false,
      isFeatured: m.isFeatured ?? false,
    }));

  return [...mergedGithub, ...unmatchedManual];
}

/**
 * Fetches public repositories from static `/data/projects.json` (generated by the backend github-sync Lambda).
 * Merges with manually curated projects (`MANUAL_PROJECTS`) with deduplication.
 * If `/data/projects.json` cannot be fetched (e.g. offline or network error), falls back to `MANUAL_PROJECTS`.
 */
export async function fetchGithubProjects(): Promise<{
  projects: GithubRepo[];
  isCached: boolean;
  error: string | null;
}> {
  try {
    const response = await fetch('/data/projects.json');
    if (!response.ok) {
      throw new Error(`Failed to load projects: HTTP ${response.status}`);
    }

    const data = await response.json();
    const rawProjects: GithubRepo[] = Array.isArray(data) ? data : data.projects || [];
    const merged = mergeProjects(rawProjects, MANUAL_PROJECTS);

    return {
      projects: merged,
      isCached: false,
      error: null,
    };
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Error fetching projects data.';

    const fallbackProjects = mergeProjects([], MANUAL_PROJECTS);
    return {
      projects: fallbackProjects,
      isCached: false,
      error: errorMessage,
    };
  }
}
